import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
    House, Calendar, ChatCircle, User, Users, Cube, ChatTeardropDots, ChatsCircle, Sparkle, UserCircle, Receipt, Lock, ShieldWarning, Heart, Broadcast, GraduationCap,
    SidebarSimple,
} from "@phosphor-icons/react";
import ErrorBoundary from "@/components/site/ErrorBoundary";


/* Poll cadence for the unread-counts badge — every 60s while the tab is
   open is light, the in-chat pollers run at 30s for real-time precision
   so this is a deliberate "tail" cadence. document.hidden short-circuits
   the next tick so background tabs don't pile up requests. */
const UNREAD_POLL_MS = 60_000;

// iter100.156e — Doctor / Patient portal sidebar collapse (mirrors the
// admin + creator treatment).
// iter100.198 — Persistence switched from `localStorage` → `sessionStorage`
// per user direction: every login lands the doctor on an EXPANDED
// sidebar (labelled = mental-map for the portal), and if they collapse
// it that preference stays only for this browser session. Next login
// (in a new tab, after logout, or after tab close) resets to
// expanded. AuthContext's `login()` + `logout()` also clear the key
// explicitly so a logout-then-relogin in the same tab still resets.
const DOCTOR_COLLAPSED_KEY = "invq_doctor_sidebar_collapsed";

// Which nav items each unread bucket maps to. Drives badge rendering
// without hard-coding paths in JSX — extends cleanly if we ever badge
// other nav items (e.g. Tickets).
// iter100.79 — Removed `cases_action`, `appointments_today`, and the
// landing-page missing count from the sidebar per user direction —
// bare numbers next to nav items were confusing (users assumed
// "unread messages" when the count actually meant "cases pending
// approval" or "landing fields still empty"). Only true message-unread
// badges remain: Lab Chat, Patient Chat, Escalations.
const UNREAD_BY_PATH = {
    "/doctor/lab-chat":       "lab_chat",
    "/doctor/patient-chat":   "patient_chat",
    "/doctor/escalations":    "escalations_open",
    "/patient/clinic-chat":   "patient_chat",
};

const PATIENT_NAV = [
    { to: "/patient", icon: House, label: "Dashboard", end: true },
    { to: "/patient/appointments", icon: Calendar, label: "Appointments" },
    // iter98.47 — `gatedBy: "treatment"` flags items that should show a
    // lock indicator until the doctor activates the patient's treatment
    // (treatment_activated_at). The actual content gating already lives
    // on the page; this is the sidebar visual cue.
    { to: "/patient/my-case", icon: Cube, label: "My Aligner Journey", gatedBy: "treatment" },
    { to: "/patient/clinic-chat", icon: ChatsCircle, label: "Chat with doctor", gatedBy: "treatment" },
    // iter98.99f3 — Patient↔CoE compliance coach chat. The page handles
    // the "no coach yet" / "treatment not started" empty-states gracefully,
    // so we surface the link unconditionally rather than gating on a
    // separate API ping. Heart icon mirrors the wizard's section icon.
    { to: "/patient/coach", icon: Heart, label: "My CoE Coach" },
    { to: "/patient/profile", icon: User, label: "Profile" },
];

/* Doctor sidebar — organised by frequency-of-use and mental model.
   Group A (Work, daily flow), B (Communications), C (Practice management),
   D (Settings). `groupLabel` marks the first item of a new group and
   triggers a caps-headline separator; `groupStart` still lands a plain
   hairline divider in the collapsed variant. */
const DOCTOR_NAV = [
    // iter100.81 — Reordered per user direction. Work group now flows
    // Patients → Appointments → Billing → Cases so daily clinic mental
    // model (patient-first) leads. Communications puts Escalations
    // first (safety-critical) then chat channels. Practice group
    // reordered: Offers → Academy → Landing Page.
    // Also renamed "Your Profile" → "My Profile" for consistency with
    // My Patients / My Appointments / My Landing Page (all use "My").
    // iter101.63 — Added caps groupLabel per group so the editorial
    // language matches the new command headers + case tabs.

    // A — Work
    { to: "/doctor", icon: House, label: "Dashboard", end: true, groupLabel: "Main" },
    { to: "/doctor/patients", icon: Users, label: "My Patients" },
    { to: "/doctor/appointments", icon: Calendar, label: "My Appointments" },
    { to: "/doctor/billing", icon: Receipt, label: "Billing" },
    { to: "/doctor/cases", icon: Cube, label: "My Aligner Cases" },

    // B — Communications
    // iter98.99f3 — Compliance escalations inbox. Coaches raise these
    // when a patient drifts or has a clinical concern; safety-critical
    // so they lead the communications group.
    { to: "/doctor/escalations", icon: ShieldWarning, label: "Escalations", groupStart: true, groupLabel: "Communication" },
    { to: "/doctor/lab-chat", icon: ChatTeardropDots, label: "Lab Chat" },
    { to: "/doctor/patient-chat", icon: ChatsCircle, label: "Patient Chat" },

    // C — Practice
    { to: "/doctor/offers", icon: Sparkle, label: "Offers", groupStart: true, groupLabel: "Growth" },
    // 2026-07-14 — Invisique Learning Academy (dual-track curriculum for
    // clear-aligner therapy).
    { to: "/doctor/academy", icon: GraduationCap, label: "Invisique Academy" },
    // iter100.3 — Doctor's own smile-sim landing page — patient-acquisition tool.
    { to: "/doctor/landing-page", icon: Broadcast, label: "My Landing Page" },

    // D — Settings
    { to: "/doctor/profile", icon: UserCircle, label: "My Profile", doctorOnly: true, groupStart: true, groupLabel: "Account" },
];

export default function PortalLayout({ role }) {
    const { user, loading } = useAuth();
    const [collapsed, setCollapsed] = useState(() => {
        // iter100.267 — Universal "default expanded" across all portals
        // (doctor + patient + admin + creator). Drop any legacy
        // localStorage value so returning users get the fresh behaviour
        // on next load, not indefinitely after clearing browser cache.
        try { localStorage.removeItem(DOCTOR_COLLAPSED_KEY); } catch { /* noop */ }
        const stored = sessionStorage.getItem(DOCTOR_COLLAPSED_KEY);
        if (stored === "1") return true;
        if (stored === "0") return false;
        return false;
    });
    useEffect(() => {
        sessionStorage.setItem(DOCTOR_COLLAPSED_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    /* Sidebar unread counts — fetched as soon as we have a user and
       re-polled every UNREAD_POLL_MS. The /me/chat-unread endpoint is
       polymorphic by role; we render whatever buckets it returns
       against the nav items in UNREAD_BY_PATH. */
    const [unread, setUnread] = useState({ lab_chat: 0, patient_chat: 0 });

    /* iter100.79 — Landing-page missing-fields badge removed per user
       direction. The count was confusing (users read it as unread
       messages). The empty-fields nudge still lives on the doctor
       dashboard "Your public microsite content" card, which is a much
       clearer surface for that signal. */
    const landingMissing = 0;

    /* iter98.47 — Treatment activation gate. Patients see two sidebar
       items locked until the doctor flips `treatment_activated_at` on
       their case: My Aligner Journey + Chat with doctor. We resolve
       activation state in one call to `/patient/me/aligner-progress`
       — same endpoint TreatmentLockedCard already uses. Doctors / staff
       / admin skip the check entirely. */
    const [treatmentLocked, setTreatmentLocked] = useState(true);
    useEffect(() => {
        if (!user || role !== "patient") { setTreatmentLocked(false); return undefined; }
        let cancelled = false;
        api.get("/patient/me/aligner-progress")
            .then((r) => {
                if (cancelled) return;
                const d = r.data || {};
                // Activated when the endpoint says `active=true` (case
                // exists AND treatment_activated_at is non-null). The
                // backend toggles `gated` to true when the case exists
                // but isn't yet activated — that's still locked from
                // the patient's perspective.
                setTreatmentLocked(!d.active);
            })
            .catch(() => { if (!cancelled) setTreatmentLocked(true); });
        return () => { cancelled = true; };
    }, [user, role]);

    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        const fetchOnce = () => {
            if (document.hidden) return;
            api.get("/me/chat-unread")
                .then((r) => { if (!cancelled) setUnread(r.data || {}); })
                .catch(() => { /* badge falls back to 0 — non-critical */ });
        };
        fetchOnce();
        const t = setInterval(fetchOnce, UNREAD_POLL_MS);
        // Refresh immediately when the tab gains focus — covers the case
        // where the doctor was away for a while and the in-chat polls
        // didn't run during that time.
        const onVisible = () => { if (!document.hidden) fetchOnce(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            clearInterval(t);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [user]);

    if (loading) return <div className="p-12 text-center text-muted-foreground">Loading...</div>;
    if (!user) {
        /* iter98.99dh — On logout, redirect to the role-specific login
           page (e.g. doctor logout → /doctor-login, patient → /patient-login)
           per user direction. Previous behavior forwarded everyone to
           "/" which is the patient-facing marketing site — felt jarring
           when a doctor signed out and saw consumer landing chrome. */
        const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "1";
        if (isLoggingOut) sessionStorage.removeItem("isLoggingOut");
        const loginPath = role === "doctor" ? "/doctor-login" : "/patient-login";
        return <Navigate to={loginPath} replace />;
    }

    // Clinic staff piggyback on the doctor portal
    const effectiveRole = user.role === "clinic_staff" ? "doctor" : user.role;
    if (effectiveRole !== role) {
        let dest = "/patient";
        if (user.role === "doctor" || user.role === "clinic_staff") dest = "/doctor";
        else if (user.role === "admin") dest = "/admin";
        else if (user.role === "staff") dest = "/coe";
        return <Navigate to={dest} replace />;
    }

    const NAV_BASE = role === "doctor" ? DOCTOR_NAV : PATIENT_NAV;
    const NAV = NAV_BASE.filter((n) => !n.doctorOnly || user.role === "doctor");

    /* iter101.85 — Gap between the sidebar and the main content is
       now conditional on the collapsed state. When the sidebar is
       expanded (w-64) we keep the generous `gap-6 / lg:gap-8` so the
       two columns read as clearly separate. When the sidebar
       collapses to just the icon rail (w-16) that gap felt bloated —
       the icons already have their own inner padding. Tightened to
       `gap-3 / lg:gap-4` in the collapsed state so the main content
       sits closer to the icon rail without touching it. */
    return (
        <div className={`mx-auto px-5 sm:px-8 py-8 lg:py-12 flex ${collapsed ? "gap-3 lg:gap-4" : "gap-6 lg:gap-8"} ${collapsed ? "max-w-[1600px]" : "max-w-7xl"}`} data-testid="portal-layout">
            <aside className={`shrink-0 transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`} data-testid="portal-sidebar" data-collapsed={collapsed ? "true" : "false"}>
                {/* iter100.77 — Sidebar becomes a fixed-height sticky rail
                    with an internal scroll region so long nav lists don't
                    push "Help & Tours" off-screen on smaller laptops. The
                    profile card + collapse toggle stay pinned at the top,
                    "Help & Tours" pinned at the bottom, and only the nav
                    list between them scrolls (thin gutter-less scrollbar). */}
                <div className={`bg-card border border-border rounded-2xl sticky top-6 lg:top-24 flex flex-col ${collapsed ? "p-2" : "p-5"}`} style={{ maxHeight: "calc(100vh - 3rem)" }}>
                    {/* Profile card (compact when collapsed) */}
                    <div className={`flex items-center border-b border-border ${collapsed ? "justify-center pb-3 mb-3" : "gap-3 pb-5 mb-4"}`}>
                        {(() => {
                            const displayName = role === "patient"
                                ? (user.name || user.doctor_name || "User")
                                : (user.doctor_name || user.name || "User");
                            const avatarImg = user.photo_data_url || (role === "patient" ? null : user.doctor_image);
                            return avatarImg ? (
                                <img
                                    src={avatarImg}
                                    alt={displayName}
                                    className={`rounded-full object-cover bg-secondary ${collapsed ? "w-9 h-9" : "w-11 h-11"}`}
                                    title={collapsed ? displayName : undefined}
                                    data-testid="sidebar-avatar-img"
                                />
                            ) : (
                                <div
                                    className={`rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold ${collapsed ? "w-9 h-9 text-sm" : "w-11 h-11"}`}
                                    title={collapsed ? displayName : undefined}
                                    data-testid="sidebar-avatar-initial"
                                >
                                    {(displayName || user.phone || "U").slice(0, 1).toUpperCase()}
                                </div>
                            );
                        })()}
                        {!collapsed && (
                            <div className="min-w-0">
                                <div className="font-semibold truncate">{role === "patient" ? (user.name || user.doctor_name || "User") : (user.doctor_name || user.name || "User")}</div>
                                <div className="text-xs text-muted-foreground truncate uppercase tracking-[0.16em] font-semibold text-[10px]">
                                    {role === "patient" ? "Patient" : (user.role === "clinic_staff" ? "Clinic Staff" : "Provider")}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* iter100.266 — Sidebar collapse toggle moved to the
                        footer (below Help & Tours) and reskinned. Text
                        label gone; now a small circular teal icon
                        button. Was previously "Collapse" text above the
                        Dashboard tab which read like a nav item. */}
                    {/* iter100.272 — Slim visible scrollbar (6px thin,
                        neutral colour) replaces the previously fully
                        invisible one so doctors can see there are more
                        tabs below the fold. Kept minimal — no fade
                        overlay (an earlier attempt with a fade gradient
                        looked like it was covering the last nav item;
                        user rejected). */}
                    <nav className="space-y-1 flex-1 min-h-0 overflow-y-auto sidebar-scroll pr-1 -mr-1" data-testid="portal-sidebar-nav-scroll">
                        {NAV.map((n) => {
                            const unreadKey = UNREAD_BY_PATH[n.to];
                            const count = unreadKey ? (unread[unreadKey] || 0) : 0;
                            const landingCount = n.to === "/doctor/landing-page" ? landingMissing : 0;
                            const locked = n.gatedBy === "treatment" && treatmentLocked;
                            return (
                                <React.Fragment key={n.to}>
                                    {!collapsed && n.groupLabel && (
                                        <div
                                            className="pt-3.5 pb-1.5 px-2 flex items-center gap-2"
                                            data-testid={`portal-nav-group-${n.groupLabel.toLowerCase()}`}
                                        >
                                            <span className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-primary/70 leading-none shrink-0">
                                                {n.groupLabel}
                                            </span>
                                            <span
                                                aria-hidden
                                                className="h-px flex-1"
                                                style={{
                                                    background: "linear-gradient(to right, hsl(var(--primary) / 0.22) 0%, transparent 100%)",
                                                }}
                                            />
                                        </div>
                                    )}
                                    {collapsed && n.groupStart && (
                                        <div className="my-1.5 h-px bg-primary/15" aria-hidden />
                                    )}
                                    <NavLink
                                        to={n.to}
                                        end={n.end}
                                        title={collapsed ? n.label : (locked ? "Unlocks after your doctor activates your treatment" : undefined)}
                                        data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                        data-locked={locked || undefined}
                                        className={({ isActive }) =>
                                            `flex items-center transition-all duration-200 ease-out ${
                                                collapsed
                                                    ? "relative justify-center h-10 rounded-xl"
                                                    : "gap-3 px-3 py-2.5 rounded-xl text-sm"
                                            } ${
                                                isActive
                                                    ? "bg-primary text-white ring-1 ring-primary/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.20),0_4px_14px_-6px_hsl(var(--primary)/0.55)]"
                                                    : "text-foreground hover:bg-primary/10 hover:text-primary"
                                            } ${
                                                locked && "opacity-65"
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <n.icon
                                                    size={18}
                                                    weight={isActive ? "fill" : "duotone"}
                                                    className={isActive ? "" : "text-primary/70"}
                                                />
                                                {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
                                                {!collapsed && locked && (
                                                    <Lock
                                                        size={13}
                                                        weight="fill"
                                                        className={isActive ? "text-white/85" : "text-muted-foreground"}
                                                        data-testid={`portal-nav-lock-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                                    />
                                                )}
                                                {!collapsed && count > 0 && (
                                                    <span
                                                        className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[10px] font-bold tabular-nums shrink-0 shadow-sm ${
                                                            isActive
                                                                ? "bg-white text-primary ring-1 ring-white/40"
                                                                : "bg-primary text-white ring-1 ring-primary/40"
                                                        }`}
                                                        data-testid={`portal-nav-badge-${unreadKey}`}
                                                    >
                                                        {count > 99 ? "99+" : count}
                                                    </span>
                                                )}
                                                {collapsed && count > 0 && (
                                                    <span
                                                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold bg-primary text-white grid place-items-center ring-2 ring-card shadow-sm"
                                                        title={`${count} unread`}
                                                        data-testid={`portal-nav-pip-${unreadKey}`}
                                                    >
                                                        {count > 9 ? "9+" : count}
                                                    </span>
                                                )}
                                                {!collapsed && landingCount > 0 && (
                                                    <span
                                                        className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums shrink-0 ${
                                                            isActive ? "bg-white text-amber-700" : "bg-amber-100 text-amber-800 border border-amber-300"
                                                        }`}
                                                        title={`${landingCount} landing page item${landingCount === 1 ? "" : "s"} to complete`}
                                                        data-testid="portal-nav-badge-landing-missing"
                                                    >
                                                        {landingCount}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </NavLink>
                                </React.Fragment>
                            );
                        })}
                    </nav>

                    {!collapsed && <HelpToursButton role={role} />}

                    {/* iter100.266 — Sidebar collapse toggle. Compact
                        circular icon button in the footer of the
                        sidebar. Teal ring on hover keeps it visually
                        anchored to the brand. Works in both expanded
                        and collapsed states. */}
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-pressed={collapsed}
                        className={`mt-3 self-center flex items-center justify-center w-8 h-8 rounded-full border border-primary/25 text-primary bg-primary/5 hover:bg-primary/15 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors ${collapsed ? "rotate-180" : ""}`}
                        data-testid="portal-sidebar-collapse-toggle"
                    >
                        <SidebarSimple size={16} weight="duotone" />
                    </button>
                </div>
            </aside>
            <main className="flex-1 min-w-0">
                <ErrorBoundary routeLabel="this page">
                    {/* iter101.86 — Expose the sidebar `collapsed`
                        state to nested routes via Outlet context so
                        wide-form pages (e.g. the New Case wizard) can
                        widen their content wrapper to `max-w-6xl`
                        when the icon rail leaves them more room. */}
                    <Outlet context={{ sidebarCollapsed: collapsed }} />
                </ErrorBoundary>
            </main>
        </div>
    );
}


/* Pill-styled doorway to the Tour Library page. The visual treatment
 * (primary tint, ring, centered Sparkle icon) is preserved from the
 * old TourLauncher button that previously sat in this slot — users
 * recognised + liked the design, so we kept it but redirected the
 * action to the library route instead of auto-launching the legacy
 * mega-tour. */
function HelpToursButton({ role }) {
    // Only doctor + patient portals have a tour library today. Other
    // roles (admin, coe, sales) get the button only when their tour
    // catalogue is populated — for now we hide it.
    const target = role === "doctor"
        ? "/doctor/tours"
        : role === "patient"
            ? "/patient/tours"   // future-proof; not yet routed
            : null;
    if (!target) return null;
    return (
        <NavLink
            to={target}
            end
            className={({ isActive }) =>
                `mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl transition-all ${
                    isActive
                        ? "text-primary-foreground bg-primary ring-1 ring-primary"
                        : "text-primary bg-primary/8 hover:bg-primary/15 ring-1 ring-primary/15 hover:ring-primary/30"
                }`
            }
            data-testid="sidebar-help-tours-btn"
            title="Browse the tour library to learn every workflow"
        >
            <Sparkle size={13} weight="fill" />
            Help & Tours
        </NavLink>
    );
}
